/**
 * googleSignIn: the Google sign-in flow shared by /login and /signup (PRP-MCP-11 G4).
 *
 * `signInWithRedirect` is the primary flow: with `authDomain` set to the app's own
 * host, the auth handler (`/__/auth/handler`, self-hosted, see
 * `web/scripts/auth-helpers.mjs`) is first-party, so its stored state survives
 * browsers that block third-party storage.
 *
 * If the redirect still fails with "missing initial state", the user is sent to
 * `?auth_retry=popup` (by the handler page's injected script, or by
 * {@link completeRedirectSignIn} when the SDK reports it) and the Google button
 * uses `signInWithPopup` there. That retry happens once: a page in retry mode never
 * starts another redirect, so there is no loop.
 */
import {
  GoogleAuthProvider,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
  type Auth,
} from 'firebase/auth';
import { authErrorMessage, isMissingInitialState, logAuthError } from './authErrors';

export const AUTH_RETRY_PARAM = 'auth_retry';

/** Shown on a retry page: popups need a click, so we ask for one. */
export const POPUP_RETRY_PROMPT = 'One more step: click Continue with Google again to finish signing in.';

/** True on a page the redirect fallback sent the user to (`?auth_retry=popup`). */
export function isPopupRetry(search: string | null | undefined): boolean {
  try {
    return new URLSearchParams(search ?? '').get(AUTH_RETRY_PARAM) === 'popup';
  } catch {
    return false;
  }
}

/** `search` with `auth_retry=popup` added (keeps `next`, `pack` and anything else). */
export function withPopupRetry(search: string | null | undefined): string {
  const params = new URLSearchParams(search ?? '');
  params.set(AUTH_RETRY_PARAM, 'popup');
  return `?${params.toString()}`;
}

/** Starts Google sign-in: the redirect flow, or the popup when retrying. */
export async function startGoogleSignIn(auth: Auth, opts: { popup: boolean }): Promise<void> {
  const provider = new GoogleAuthProvider();
  if (opts.popup) {
    await signInWithPopup(auth, provider);
    return;
  }
  await signInWithRedirect(auth, provider);
}

export type RedirectOutcome =
  | { kind: 'none' }
  | { kind: 'signed-in' }
  | { kind: 'retry-popup' }
  | { kind: 'error'; message: string };

/**
 * Finishes a redirect sign-in on page load. A missing-initial-state failure asks for
 * the popup retry, unless this page already is the retry (then it is an error).
 */
export async function completeRedirectSignIn(
  auth: Auth,
  search: string | null | undefined,
): Promise<RedirectOutcome> {
  try {
    const cred = await getRedirectResult(auth);
    return cred ? { kind: 'signed-in' } : { kind: 'none' };
  } catch (err) {
    logAuthError(err, 'google redirect');
    if (isMissingInitialState(err) && !isPopupRetry(search)) return { kind: 'retry-popup' };
    return { kind: 'error', message: authErrorMessage(err) };
  }
}
